import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Badge,
  Icon,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  FaMagic,
  FaIceCream,
  FaCoffee,
  FaFileAlt,
  FaLeaf,
  FaMapMarkerAlt,
  FaMoon,
  FaArrowDown,
} from 'react-icons/fa';
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import WrappedSection from '../components/WrappedSection';
import StorySection from '../components/StorySection';
import CountUpNumber from '../components/CountUpNumber';
import { wrapped2025Sections } from '../data/wrapped2025Sections';

const MotionBox = motion(Box);
const MotionText = motion(Text);
const MotionHeading = motion(Heading);

// Map icon names to components
const iconMap = {
  FaMagic,
  FaIceCream,
  FaCoffee,
  FaFileAlt,
  FaLeaf,
  FaMapMarkerAlt,
  FaMoon,
};

function CilWrapped2025() {
  const [shouldReduceMotion] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    return false;
  });

  const demographicData = [
    { name: 'Minorenni', value: 43.52, color: '#36a9e1' },
    { name: 'Maggiorenni', value: 56.48, color: '#ffcc00' },
  ];

  // Get section config by id
  const getSectionConfig = (id) =>
    wrapped2025Sections.find((s) => s.id === id) || {};

  const scrollToNext = () => {
    const sections = document.querySelectorAll('[data-wrapped-section]');
    if (sections.length > 1) {
      sections[1].scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box w="100%" minH="100vh" bg="gray.50">
      <WrappedSection
        id="hero"
        data-wrapped-section
        bgGradient="linear(to-b, #3b235a, #1a202c)"
        position="relative"
        overflow="hidden"
      >
        <Container maxW="container.xl" textAlign="center" zIndex={2}>
          <VStack
            spacing={6}
            maxW={{ base: '100%', md: '800px', lg: '900px' }}
            mx="auto"
          >
            <MotionHeading
              as="h1"
              fontSize={{ base: '4xl', md: '6xl', lg: '8xl' }}
              fontWeight="bold"
              color="white"
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {getSectionConfig('hero').title}
            </MotionHeading>
            <VStack spacing={4} align="center">
              <MotionText
                fontSize={{ base: 'lg', md: 'xl', lg: '2xl' }}
                color="white"
                opacity={0.95}
                lineHeight="1.6"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {getSectionConfig('hero').introText}
              </MotionText>
              <MotionText
                fontSize={{ base: 'lg', md: 'xl', lg: '2xl' }}
                color="white"
                opacity={0.95}
                lineHeight="1.6"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                {getSectionConfig('hero').introText2}
              </MotionText>
              <MotionText
                fontSize={{ base: 'lg', md: 'xl', lg: '2xl' }}
                color="white"
                opacity={0.95}
                lineHeight="1.6"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {getSectionConfig('hero').introText3}
              </MotionText>
            </VStack>
            <MotionBox
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              mt={4}
            >
              <Button
                onClick={scrollToNext}
                size="lg"
                bg="brand.100"
                color="black"
                _hover={{ bg: 'brand.200', transform: 'scale(1.05)' }}
                rightIcon={<FaArrowDown />}
              >
                Scorri ↓
              </Button>
            </MotionBox>
          </VStack>
        </Container>
      </WrappedSection>

      <WrappedSection
        id="eventi-organizzati"
        data-wrapped-section
        bg={getSectionConfig('eventi-organizzati').bgColor}
        bgImage={getSectionConfig('eventi-organizzati').bgImage}
      >
        <Container maxW="container.xl" textAlign="center">
          <StorySection
            section={{
              ...getSectionConfig('eventi-organizzati'),
              fontSize: { base: '6xl', md: '8xl', lg: '10xl' },
              fontWeight: 'bold',
              color: 'black',
            }}
            shouldReduceMotion={shouldReduceMotion}
          >
            <CountUpNumber
              value={getSectionConfig('eventi-organizzati').value}
              fontSize={{ base: '6xl', md: '8xl', lg: '10xl' }}
              fontWeight="bold"
              color="black"
            />
          </StorySection>
        </Container>
      </WrappedSection>

      <WrappedSection
        id="giorno-eventi"
        data-wrapped-section
        bg={getSectionConfig('giorno-eventi').bgColor}
        bgImage={getSectionConfig('giorno-eventi').bgImage}
      >
        <Container maxW="container.xl" textAlign="center">
          <VStack spacing={6} align="center">
            <VStack spacing={4} textAlign="center">
              <Heading
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                color="brand.500"
                fontWeight="normal"
              >
                Il giorno più vivo per CIL è
              </Heading>
              <Heading
                fontSize={{ base: '4xl', md: '6xl', lg: '8xl' }}
                color="brand.500"
                fontWeight="bold"
              >
                {getSectionConfig('giorno-eventi').title}
              </Heading>
              <CountUpNumber
                value={getSectionConfig('giorno-eventi').value}
                fontSize={{ base: '4xl', md: '6xl', lg: '8xl' }}
                fontWeight="bold"
                color="brand.500"
              />
            </VStack>
            <MotionBox
              maxW={{ base: '100%', md: '900px', lg: '1000px' }}
              textAlign="center"
              px={{ base: 4, md: 6, lg: 8 }}
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 10 }}
              animate={shouldReduceMotion ? {} : { opacity: 1, y: 0 }}
              transition={
                shouldReduceMotion
                  ? {}
                  : { duration: 0.5, delay: 0.15, ease: 'easeOut' }
              }
            >
              <Text
                as="p"
                fontSize={{ base: 'xl', md: '2xl', lg: '3xl' }}
                fontWeight="normal"
                color="gray.700"
                opacity={0.95}
                lineHeight="1.5"
              >
                {getSectionConfig('giorno-eventi').description}
              </Text>
            </MotionBox>
          </VStack>
        </Container>
      </WrappedSection>

      <WrappedSection
        id="evento-lungo"
        data-wrapped-section
        bgGradient={getSectionConfig('evento-lungo').bgGradient}
        bgImage={getSectionConfig('evento-lungo').bgImage}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="container.xl" textAlign="center" zIndex={2}>
          <VStack spacing={8} align="center">
            {getSectionConfig('evento-lungo').label && (
              <MotionBox
                textAlign="center"
                initial={shouldReduceMotion ? {} : { opacity: 0, y: 10 }}
                animate={shouldReduceMotion ? {} : { opacity: 1, y: 0 }}
                transition={
                  shouldReduceMotion
                    ? {}
                    : { duration: 0.5, delay: 0.1, ease: 'easeOut' }
                }
              >
                <Text
                  as="h2"
                  fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                  fontWeight="bold"
                  color="gray.300"
                  letterSpacing="wide"
                  textTransform="uppercase"
                >
                  {getSectionConfig('evento-lungo').label}
                </Text>
              </MotionBox>
            )}
            <VStack spacing={6}>
              <CountUpNumber
                value={getSectionConfig('evento-lungo').value}
                fontSize={{ base: '6xl', md: '8xl', lg: '10xl' }}
                fontWeight="bold"
                color="white"
              />
              <Text
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                color="brand.100"
              >
                {getSectionConfig('evento-lungo').unit}
              </Text>
              {getSectionConfig('evento-lungo').detail && (
                <Badge
                  fontSize={{ base: 'md', md: 'lg' }}
                  px={4}
                  py={2}
                  bg="brand.100"
                  color="black"
                >
                  {getSectionConfig('evento-lungo').detail}
                </Badge>
              )}
            </VStack>
            {getSectionConfig('evento-lungo').description && (
              <MotionBox
                maxW={{ base: '100%', md: '900px', lg: '1000px' }}
                textAlign="center"
                px={{ base: 4, md: 6, lg: 8 }}
                initial={shouldReduceMotion ? {} : { opacity: 0, y: 10 }}
                animate={shouldReduceMotion ? {} : { opacity: 1, y: 0 }}
                transition={
                  shouldReduceMotion
                    ? {}
                    : { duration: 0.5, delay: 0.15, ease: 'easeOut' }
                }
              >
                <Text
                  as="p"
                  fontSize={{ base: 'xl', md: '2xl', lg: '3xl' }}
                  fontWeight="normal"
                  color="gray.300"
                  opacity={0.95}
                  lineHeight="1.5"
                >
                  {getSectionConfig('evento-lungo').description}
                </Text>
              </MotionBox>
            )}
          </VStack>
        </Container>
      </WrappedSection>

      <WrappedSection
        id="bustine-magic"
        data-wrapped-section
        bg={getSectionConfig('bustine-magic').bgColor}
        bgImage={getSectionConfig('bustine-magic').bgImage}
      >
        <Container maxW="container.xl" textAlign="center">
          <StorySection
            section={{
              ...getSectionConfig('bustine-magic'),
              fontSize: { base: '6xl', md: '8xl', lg: '10xl' },
              fontWeight: 'bold',
              color: 'white',
            }}
            shouldReduceMotion={shouldReduceMotion}
          >
            <VStack spacing={8}>
              {getSectionConfig('bustine-magic').icon && (
                <Icon
                  as={iconMap[getSectionConfig('bustine-magic').icon]}
                  w={{ base: 16, md: 20, lg: 24 }}
                  h={{ base: 16, md: 20, lg: 24 }}
                  color="white"
                />
              )}
              <CountUpNumber
                value={getSectionConfig('bustine-magic').value}
                fontSize={{ base: '6xl', md: '8xl', lg: '10xl' }}
                fontWeight="bold"
                color="white"
              />
            </VStack>
          </StorySection>
        </Container>
      </WrappedSection>

      <WrappedSection
        id="ghiaccioli"
        data-wrapped-section
        bg={getSectionConfig('ghiaccioli').bgColor}
        bgImage={getSectionConfig('ghiaccioli').bgImage}
      >
        <Container maxW="container.xl" textAlign="center">
          <StorySection
            section={{
              ...getSectionConfig('ghiaccioli'),
              fontSize: { base: '6xl', md: '8xl', lg: '10xl' },
              fontWeight: 'bold',
              color: 'white',
            }}
            shouldReduceMotion={shouldReduceMotion}
          >
            <VStack spacing={8}>
              {getSectionConfig('ghiaccioli').icon && (
                <Icon
                  as={iconMap[getSectionConfig('ghiaccioli').icon]}
                  w={{ base: 16, md: 20, lg: 24 }}
                  h={{ base: 16, md: 20, lg: 24 }}
                  color="white"
                />
              )}
              <CountUpNumber
                value={getSectionConfig('ghiaccioli').value}
                fontSize={{ base: '6xl', md: '8xl', lg: '10xl' }}
                fontWeight="bold"
                color="white"
              />
            </VStack>
          </StorySection>
        </Container>
      </WrappedSection>

      <WrappedSection
        id="caffe"
        data-wrapped-section
        bg={getSectionConfig('caffe').bgColor}
        bgImage={getSectionConfig('caffe').bgImage}
      >
        <Container maxW="container.xl" textAlign="center">
          <StorySection
            section={{
              ...getSectionConfig('caffe'),
              fontSize: { base: '5xl', md: '7xl', lg: '9xl' },
              fontWeight: 'bold',
              color: '#3b235a',
            }}
            shouldReduceMotion={shouldReduceMotion}
          >
            <VStack spacing={8}>
              {getSectionConfig('caffe').icon && (
                <Icon
                  as={iconMap[getSectionConfig('caffe').icon]}
                  w={{ base: 12, md: 16, lg: 20 }}
                  h={{ base: 12, md: 16, lg: 20 }}
                  color="#3b235a"
                />
              )}
              <CountUpNumber
                value={getSectionConfig('caffe').value}
                format={(v) => `${v.toFixed(2)} kg`}
                fontSize={{ base: '5xl', md: '7xl', lg: '9xl' }}
                fontWeight="bold"
                color="#3b235a"
              />
            </VStack>
          </StorySection>
        </Container>
      </WrappedSection>

      <WrappedSection
        id="bandi"
        data-wrapped-section
        bg={getSectionConfig('bandi').bgColor}
        bgImage={getSectionConfig('bandi').bgImage}
      >
        <Container maxW="container.xl" textAlign="center">
          <StorySection
            section={{
              ...getSectionConfig('bandi'),
              fontSize: { base: '6xl', md: '8xl', lg: '10xl' },
              fontWeight: 'bold',
              color: 'white',
            }}
            shouldReduceMotion={shouldReduceMotion}
          >
            <VStack spacing={8}>
              {getSectionConfig('bandi').icon && (
                <Icon
                  as={iconMap[getSectionConfig('bandi').icon]}
                  w={{ base: 12, md: 16, lg: 20 }}
                  h={{ base: 12, md: 16, lg: 20 }}
                  color="white"
                />
              )}
              <CountUpNumber
                value={getSectionConfig('bandi').value}
                fontSize={{ base: '6xl', md: '8xl', lg: '10xl' }}
                fontWeight="bold"
                color="white"
              />
            </VStack>
          </StorySection>
        </Container>
      </WrappedSection>

      <WrappedSection
        id="piante"
        data-wrapped-section
        bg={getSectionConfig('piante').bgColor}
        bgImage={getSectionConfig('piante').bgImage}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(59, 35, 90, 0.1)"
          zIndex={1}
        />
        <Container
          maxW="container.xl"
          textAlign="center"
          zIndex={2}
          position="relative"
        >
          <StorySection
            section={{
              ...getSectionConfig('piante'),
              fontSize: { base: '6xl', md: '8xl', lg: '10xl' },
              fontWeight: 'bold',
              color: '#3b235a',
            }}
            shouldReduceMotion={shouldReduceMotion}
          >
            <VStack spacing={8}>
              {getSectionConfig('piante').icon && (
                <Icon
                  as={iconMap[getSectionConfig('piante').icon]}
                  w={{ base: 16, md: 20, lg: 24 }}
                  h={{ base: 16, md: 20, lg: 24 }}
                  color="#3eb297"
                />
              )}
              <CountUpNumber
                value={getSectionConfig('piante').value}
                fontSize={{ base: '6xl', md: '8xl', lg: '10xl' }}
                fontWeight="bold"
                color="#3b235a"
              />
            </VStack>
          </StorySection>
        </Container>
      </WrappedSection>

      <WrappedSection
        id="fascia-eta"
        data-wrapped-section
        bg={getSectionConfig('fascia-eta').bgColor}
        bgImage={getSectionConfig('fascia-eta').bgImage}
      >
        <Container maxW="container.xl" textAlign="center">
          <StorySection
            section={{
              ...getSectionConfig('fascia-eta'),
              fontSize: { base: '4xl', md: '6xl', lg: '8xl' },
              fontWeight: 'bold',
              color: 'brand.500',
            }}
            shouldReduceMotion={shouldReduceMotion}
          >
            <VStack spacing={4} textAlign="center">
              <Heading
                fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                color="brand.500"
              >
                {getSectionConfig('fascia-eta').title}
              </Heading>
              <CountUpNumber
                value={getSectionConfig('fascia-eta').value}
                fontSize={{ base: '4xl', md: '6xl', lg: '8xl' }}
                fontWeight="bold"
                color="brand.500"
              />
            </VStack>
          </StorySection>
        </Container>
      </WrappedSection>

      <WrappedSection
        id="mese-soci"
        data-wrapped-section
        bg={getSectionConfig('mese-soci').bgColor}
        bgImage={getSectionConfig('mese-soci').bgImage}
      >
        <Container maxW="container.xl" textAlign="center">
          <VStack spacing={6} align="center">
            <VStack spacing={4} textAlign="center">
              <Heading
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                color="black"
                fontWeight="normal"
                maxW={{ base: '100%', md: '900px' }}
              >
                {getSectionConfig('mese-soci').title}
              </Heading>
              <CountUpNumber
                value={getSectionConfig('mese-soci').value}
                fontSize={{ base: '4xl', md: '6xl', lg: '8xl' }}
                fontWeight="bold"
                color="black"
              />
            </VStack>
            <MotionBox
              maxW={{ base: '100%', md: '900px', lg: '1000px' }}
              textAlign="center"
              px={{ base: 4, md: 6, lg: 8 }}
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 10 }}
              animate={shouldReduceMotion ? {} : { opacity: 1, y: 0 }}
              transition={
                shouldReduceMotion
                  ? {}
                  : { duration: 0.5, delay: 0.15, ease: 'easeOut' }
              }
            >
              <Text
                as="p"
                fontSize={{ base: 'xl', md: '2xl', lg: '3xl' }}
                fontWeight="normal"
                color="black"
                opacity={0.95}
                lineHeight="1.5"
              >
                {getSectionConfig('mese-soci').description}
              </Text>
            </MotionBox>
          </VStack>
        </Container>
      </WrappedSection>

      <WrappedSection
        id="composizione-anagrafica"
        data-wrapped-section
        bg={getSectionConfig('composizione-anagrafica').bgColor}
        bgImage={getSectionConfig('composizione-anagrafica').bgImage}
      >
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <StorySection
              section={{
                ...getSectionConfig('composizione-anagrafica'),
                fontSize: { base: 'lg', md: 'xl', lg: '2xl' },
                fontWeight: 'normal',
                color: 'gray.700',
              }}
              shouldReduceMotion={shouldReduceMotion}
            >
              <VStack spacing={4} textAlign="center">
                <Heading
                  fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                  color="brand.500"
                >
                  {getSectionConfig('composizione-anagrafica').title}
                </Heading>
                <HStack spacing={8} justify="center" flexWrap="wrap">
                  <VStack>
                    <Text
                      fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                      fontWeight="bold"
                      color="#36a9e1"
                    >
                      {
                        getSectionConfig('composizione-anagrafica').percentages
                          ?.minorenni
                      }
                      %
                    </Text>
                    <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.700">
                      Minorenni
                    </Text>
                  </VStack>
                  <VStack>
                    <Text
                      fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                      fontWeight="bold"
                      color="#ffcc00"
                    >
                      {
                        getSectionConfig('composizione-anagrafica').percentages
                          ?.maggiorenni
                      }
                      %
                    </Text>
                    <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.700">
                      Maggiorenni
                    </Text>
                  </VStack>
                </HStack>
              </VStack>
            </StorySection>
            <Box
              w="100%"
              maxW={{ base: '500px', md: '700px', lg: '800px' }}
              h={{ base: '400px', md: '500px', lg: '600px' }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={demographicData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}%`}
                    outerRadius={{ base: 120, md: 150, lg: 180 }}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {demographicData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </VStack>
        </Container>
      </WrappedSection>

      <WrappedSection
        id="eta-media"
        data-wrapped-section
        bg={getSectionConfig('eta-media').bgColor}
        bgImage={getSectionConfig('eta-media').bgImage}
      >
        <Container maxW="container.xl" textAlign="center">
          <StorySection
            section={{
              ...getSectionConfig('eta-media'),
              fontSize: { base: '5xl', md: '7xl', lg: '9xl' },
              fontWeight: 'bold',
              color: 'white',
            }}
            shouldReduceMotion={shouldReduceMotion}
          >
            <CountUpNumber
              value={getSectionConfig('eta-media').value}
              format={(v) => `${v.toFixed(2)} anni`}
              fontSize={{ base: '5xl', md: '7xl', lg: '9xl' }}
              fontWeight="bold"
              color="white"
            />
          </StorySection>
        </Container>
      </WrappedSection>

      <WrappedSection
        id="socio-attivo"
        data-wrapped-section
        bg={getSectionConfig('socio-attivo').bgColor}
        bgImage={getSectionConfig('socio-attivo').bgImage}
      >
        <Container maxW="container.xl" textAlign="center">
          <VStack spacing={6} align="center">
            <VStack spacing={4} textAlign="center">
              <Heading
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                color="white"
                fontWeight="normal"
                maxW={{ base: '100%', md: '900px' }}
              >
                {getSectionConfig('socio-attivo').introText}
              </Heading>
              <Heading
                fontSize={{ base: '4xl', md: '6xl', lg: '8xl' }}
                color="white"
                fontWeight="bold"
              >
                {getSectionConfig('socio-attivo').title}
              </Heading>
              <HStack
                spacing={2}
                justify="center"
                align="baseline"
                flexWrap="wrap"
              >
                <Text
                  fontSize={{ base: 'xl', md: '2xl', lg: '3xl' }}
                  color="white"
                  fontWeight="normal"
                >
                  con
                </Text>
                <CountUpNumber
                  value={getSectionConfig('socio-attivo').value}
                  fontSize={{ base: '4xl', md: '6xl', lg: '8xl' }}
                  fontWeight="bold"
                  color="white"
                />
                <Text
                  fontSize={{ base: 'xl', md: '2xl', lg: '3xl' }}
                  color="white"
                  fontWeight="normal"
                >
                  {getSectionConfig('socio-attivo').unit}
                </Text>
              </HStack>
            </VStack>
            {getSectionConfig('socio-attivo').description && (
              <MotionBox
                maxW={{ base: '100%', md: '900px', lg: '1000px' }}
                textAlign="center"
                px={{ base: 4, md: 6, lg: 8 }}
                initial={shouldReduceMotion ? {} : { opacity: 0, y: 10 }}
                animate={shouldReduceMotion ? {} : { opacity: 1, y: 0 }}
                transition={
                  shouldReduceMotion
                    ? {}
                    : { duration: 0.5, delay: 0.15, ease: 'easeOut' }
                }
              >
                <Text
                  as="p"
                  fontSize={{ base: 'xl', md: '2xl', lg: '3xl' }}
                  fontWeight="normal"
                  color="white"
                  opacity={0.95}
                  lineHeight="1.5"
                >
                  {getSectionConfig('socio-attivo').description}
                </Text>
              </MotionBox>
            )}
          </VStack>
        </Container>
      </WrappedSection>

      <WrappedSection
        id="outro"
        data-wrapped-section
        bgGradient="linear(to-b, #ffcc00, #3eb297)"
      >
        <Container maxW="container.xl" textAlign="center">
          <VStack
            spacing={8}
            maxW={{ base: '100%', md: '800px', lg: '900px' }}
            mx="auto"
          >
            <Heading
              fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }}
              color="black"
              fontWeight="bold"
            >
              {getSectionConfig('outro').title}
            </Heading>
            <VStack spacing={4} align="center">
              <Text
                fontSize={{ base: 'lg', md: 'xl', lg: '2xl' }}
                color="black"
                lineHeight="1.6"
              >
                {getSectionConfig('outro').closingText}
              </Text>
              <Text
                fontSize={{ base: 'lg', md: 'xl', lg: '2xl' }}
                color="black"
                lineHeight="1.6"
              >
                {getSectionConfig('outro').closingText2}
              </Text>
              <Text
                fontSize={{ base: 'lg', md: 'xl', lg: '2xl' }}
                color="black"
                lineHeight="1.6"
                mt={4}
              >
                {getSectionConfig('outro').closingText3}
              </Text>
              <Text
                fontSize={{ base: 'lg', md: 'xl', lg: '2xl' }}
                color="black"
                lineHeight="1.6"
              >
                {getSectionConfig('outro').closingText4}
              </Text>
            </VStack>
            <VStack spacing={4} w="100%" maxW="600px" mx="auto" mt={6}>
              <Button
                as="a"
                href={
                  getSectionConfig('outro').ctaTesseramento?.link ||
                  '/tesseramento'
                }
                size="lg"
                w="100%"
                bg="black"
                color="white"
                fontSize={{ base: 'lg', md: 'xl' }}
                py={6}
                fontWeight="bold"
                borderRadius="md"
                boxShadow="lg"
                _hover={{
                  bg: 'gray.800',
                  transform: 'translateY(-2px)',
                  boxShadow: 'xl',
                }}
                transition="all 0.2s"
              >
                👉{' '}
                {getSectionConfig('outro').ctaTesseramento?.text ||
                  'Tesserati a CIL'}
              </Button>
              <Button
                as="a"
                href={
                  getSectionConfig('outro').ctaRinnova?.link ||
                  '/account/tesseramenti'
                }
                size="lg"
                w="100%"
                bg="white"
                color="black"
                fontSize={{ base: 'lg', md: 'xl' }}
                py={6}
                fontWeight="bold"
                borderRadius="md"
                border="2px solid black"
                boxShadow="md"
                _hover={{
                  bg: 'gray.50',
                  transform: 'translateY(-2px)',
                  boxShadow: 'xl',
                  borderColor: 'gray.800',
                }}
                transition="all 0.2s"
              >
                🔄{' '}
                {getSectionConfig('outro').ctaRinnova?.text ||
                  'Rinnova la tessera'}
              </Button>
              <Button
                as="a"
                href={
                  getSectionConfig('outro').ctaInstagram?.link ||
                  'https://www.instagram.com/cil_associazione/'
                }
                target="_blank"
                rel="noopener noreferrer"
                size="lg"
                w="100%"
                bg="transparent"
                color="black"
                fontSize={{ base: 'lg', md: 'xl' }}
                py={6}
                fontWeight="semibold"
                borderRadius="md"
                border="2px solid black"
                _hover={{
                  bg: 'black',
                  color: 'white',
                  transform: 'translateY(-2px)',
                  boxShadow: 'xl',
                }}
                leftIcon={<Icon as={iconMap.FaInstagram} />}
                transition="all 0.2s"
              >
                {getSectionConfig('outro').ctaInstagram?.text ||
                  'Seguici su Instagram: @cil_associazione'}
              </Button>
            </VStack>
          </VStack>
        </Container>
      </WrappedSection>
    </Box>
  );
}

export default CilWrapped2025;
